import { createReducer, createActions } from "reduxsauce";
import Immutable from "seamless-immutable";

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  changeName: ["data"],

  authenticationCheckRequest: null,
  authenticationCheckSuccess: ["payload"],
  authenticationCheckFailure: ["error"],

  loginRequest: ["data", "resolve"],
  loginSuccess: ["payload"],
  loginFailure: ["error"],

  registerRequest: ["data", "resolve"],
  registerSuccess: ["payload"],
  registerFailure: ["error"],

  refreshTokenRequest: null,
  refreshTokenSuccess: ["payload"],
  refreshTokenFailure: ["error"],

  logout: null,

  checkProfileCompleteRequest: ["data"],
  checkProfileCompleteSuccess: ["payload"],
  checkProfileCompleteFailure: ["error"],

  resetLinkRequest: ["data"],
  resetLinkSuccess: ["payload"],
  resetLinkFailure: ["error"],

  passwordResetRequest: ["data"],
  passwordResetSuccess: ["payload"],
  passwordResetFailure: ["error"],

  authenticateEmailRequest: ["data"],
  authenticateEmailSuccess: ["payload"],
  authenticateEmailFailure: ["error"],

  resetRedux: null,

  socialAuthLoginRequest: ["data", "resolve"],
  socialAuthLoginSuccess: ["payload"],
  socialAuthLoginFailure: ["error"],

  getUserDetailsRequest: null,
  getUserDetailsSuccess: ["payload"],
  getUserDetailsFailure: ["error"],

  updateUserDetailsRequest: ["data", "resolve"],
  updateUserDetailsSuccess: ["payload"],
  updateUserDetailsFailure: ["error"],
});

export const AuthenticationTypes = Types;
export default Creators;

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  tokens: {
    access: null,
    refresh: null,
  },
  userDetails: {},
  action: null,
  fetching: false,
  error: null,
  isAuthenticated: false,
  emailAuthenticated: false,
  passwordReset: false,
});

/* ------------- Reducers ------------- */

export const authenticationCheckRequest = (state) =>
  state.merge({ fetching: true });

export const authenticationCheckSuccess = (state, action) => {
  const { payload } = action;
  return state.merge({
    isAuthenticated: true,
    error: null,
    fetching: false,
    registrationComplete: payload.profile_details.registration_complete,
    tokens: payload.tokens,
    userDetails: payload.user_details,
  });
};

export const authenticationCheckFailure = (state, data) =>
  state.merge({
    error: data.error,
    isAuthentication: false,
  });

export const loginRequest = (state) => state.merge({ fetching: true });

export const loginSuccess = (state, data) => {
  const { payload } = data;
  return state.merge({
    fetching: false,
    error: null,
    isAuthenticated: true,
    tokens: payload,
  });
};

export const loginFailure = (state, data) =>
  state.merge({
    fetching: false,
    error: data,
    isAuthenticated: false,
  });

export const resetLinkRequest = (state, action) =>
  state.merge({ fetching: true, action: action });

export const resetLinkSuccess = (state, action) =>
  state.merge({ fetching: false, error: false });

export const resetLinkFailure = (state, action) =>
  state.merge({ fetching: false, error: action.error, password_reset: false });

export const passwordResetRequest = (state, action) =>
  state.merge({ fetching: true, action: action });

export const passwordResetSuccess = (state, action) =>
  state.merge({ fetching: false, passwordReset: true });

export const passwordResetFailure = (state, action) =>
  state.merge({ fetching: false, error: action.error, passwordReset: false });

export const registerRequest = (state) => state.merge({ fetching: true });

export const registerSuccess = (state, { payload }) => {
  return state.merge({
    fetching: false,
    error: null,
    isAuthenticated: true,
    tokens: payload,
  });
};

export const registerFailure = (state, data) =>
  state.merge({ fetching: false, error: data.error });

export const refreshTokenRequest = (state) => state.merge({ fetching: true });

export const refreshTokenSuccess = (state, { payload }) => {
  return state.merge({
    fetching: false,
    tokens: {
      ...state.tokens,
      access: payload.access,
    },
  });
};

export const refreshTokenFailure = (state, data) =>
  state.merge({ fetching: false, error: data });

export const logout = (state) =>
  state.merge({
    isAuthenticated: false,
    username: null,
    accessToken: null,
    refreshToken: null,
    registrationComplete: false,
    user: null,
  });

export const authenticateEmailRequest = (state, action) =>
  state.merge({ fetching: true });

export const authenticateEmailSuccess = (state, action) =>
  state.merge({ fetching: false, error: null, emailAuthenticated: true });

export const authenticateEmailFailure = (state, action) =>
  state.merge({ fetching: false, error: action.error });

export const resetRedux = (state, action) => {};

export const socialAuthLoginRequest = (state, action) => {
  return state.merge({ fetching: true });
};

export const socialAuthLoginSuccess = (state, { payload }) => {
  return state.merge({
    fetching: false,
    error: null,
    isAuthenticated: true,
    tokens: payload,
  });
};

export const socialAuthLoginFailure = (state, { error }) => {
  return state.merge({ fetching: false, error: error });
};

export const getUserDetailsRequest = (state, action) => {
  return state.merge({ fetching: true });
};

export const getUserDetailsSuccess = (state, { payload }) => {
  return state.merge({
    fetching: false,
    error: null,
    userDetails: payload,
  });
};

export const getUserDetailsFailure = (state, { error }) => {
  return state.merge({ fetching: false, error: error });
};

export const updateUserDetailsRequest = (state, action) => {
  return state.merge({ fetching: true });
};

export const updateUserDetailsSuccess = (state, { payload }) => {
  return state.merge({
    fetching: false,
    error: null,
    userDetails: payload,
  });
};

export const updateUserDetailsFailure = (state, { error }) => {
  return state.merge({ fetching: false, error: error });
};

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.AUTHENTICATION_CHECK_REQUEST]: authenticationCheckRequest,
  [Types.AUTHENTICATION_CHECK_SUCCESS]: authenticationCheckSuccess,
  [Types.AUTHENTICATION_CHECK_FAILURE]: authenticationCheckFailure,

  [Types.RESET_LINK_REQUEST]: resetLinkRequest,
  [Types.RESET_LINK_SUCCESS]: resetLinkSuccess,
  [Types.RESET_LINK_FAILURE]: resetLinkFailure,

  [Types.PASSWORD_RESET_REQUEST]: passwordResetRequest,
  [Types.PASSWORD_RESET_SUCCESS]: passwordResetSuccess,
  [Types.PASSWORD_RESET_FAILURE]: passwordResetFailure,

  [Types.LOGIN_REQUEST]: loginRequest,
  [Types.LOGIN_SUCCESS]: loginSuccess,
  [Types.LOGIN_FAILURE]: loginFailure,

  [Types.REGISTER_REQUEST]: registerRequest,
  [Types.REGISTER_SUCCESS]: registerSuccess,
  [Types.REGISTER_FAILURE]: registerFailure,

  [Types.REFRESH_TOKEN_REQUEST]: refreshTokenRequest,
  [Types.REFRESH_TOKEN_SUCCESS]: refreshTokenSuccess,
  [Types.REFRESH_TOKEN_FAILURE]: refreshTokenFailure,

  [Types.LOGOUT]: logout,

  [Types.AUTHENTICATE_EMAIL_REQUEST]: authenticateEmailRequest,
  [Types.AUTHENTICATE_EMAIL_SUCCESS]: authenticateEmailSuccess,
  [Types.AUTHENTICATE_EMAIL_FAILURE]: authenticateEmailFailure,

  [Types.SOCIAL_AUTH_LOGIN_REQUEST]: socialAuthLoginRequest,
  [Types.SOCIAL_AUTH_LOGIN_SUCCESS]: socialAuthLoginSuccess,
  [Types.SOCIAL_AUTH_LOGIN_FAILURE]: socialAuthLoginFailure,

  [Types.RESET_REDUX]: resetRedux,

  [Types.GET_USER_DETAILS_REQUEST]: getUserDetailsRequest,
  [Types.GET_USER_DETAILS_SUCCESS]: getUserDetailsSuccess,
  [Types.GET_USER_DETAILS_FAILURE]: getUserDetailsFailure,

  [Types.UPDATE_USER_DETAILS_REQUEST]: updateUserDetailsRequest,
  [Types.UPDATE_USER_DETAILS_SUCCESS]: updateUserDetailsSuccess,
  [Types.UPDATE_USER_DETAILS_FAILURE]: updateUserDetailsFailure,
});

/* ------------- Selectors ------------- */

export const AuthenticationSelectors = {
  getAction: (state) => state.authentication.action,
  getFetching: (state) => state.authentication.fetching,
  getError: (state) => state.authentication.error,
  getUsername: (state) => state.authentication.user,
  getTokens: (state) => state.authentication.tokens,
  getUserDetails: (state) => state.authentication.userDetails,
  getAuthenticated: (state) => state.authentication.isAuthenticated,
  getAccessToken: (state) => state.authentication.tokens.access,
  getRefreshToken: (state) => state.authentication.tokens.refresh,
  getEmailAuthenticated: (state) => state.authentication.emailAuthenticated,
  getPasswordReset: (state) => state.authentication.passwordReset,
};
